// src/prototype/artifacts/types/artifactTypes.ts
// ============================================================
// DVA Artifact Schema v1 — Execute Artifacts
// Core type system for structured execute artifact generation,
// UI rendering, modal preview, and future AI / export support.
// ============================================================

export type ArtifactScope = "enterprise" | "initiative";

export type ArtifactStatus = "draft" | "review" | "final";

export type ArtifactType =
  | "strategic_execution_plan"
  | "executive_one_pager"
  | "cfo_roi_summary"
  | "board_rationale"
  | "assumptions_appendix"
  | "implementation_packet";

// ── Metadata ──────────────────────────────────────────────────

export interface DvaArtifactMetadata {
  version: number;
  status: ArtifactStatus;
  generatedAt: string;          // ISO timestamp
  generatedBy: "system" | "user" | "ai";
  modelUsed?: string;
  promptVersion?: string;
  sourcePacketIds?: string[];
  confidenceScore?: number;
  lastEditedAt?: string;
  lastEditedBy?: string;
}

// ── Section Types ─────────────────────────────────────────────

export type ArtifactSectionType =
  | "narrative"
  | "kpi_grid"
  | "bullet_list"
  | "table"
  | "timeline"
  | "risk_list"
  | "assumption_list"
  | "mixed";

// ── Block Types ───────────────────────────────────────────────

export type ArtifactBlockType =
  | "paragraph"
  | "stat"
  | "stat_group"
  | "bullet_list"
  | "table"
  | "timeline_phase"
  | "risk_item"
  | "assumption_item"
  | "tag_row"
  | "callout";

// ── Block ─────────────────────────────────────────────────────

export interface DvaArtifactBlock {
  blockId: string;
  blockType: ArtifactBlockType;
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
}

// ── Section ───────────────────────────────────────────────────

export interface DvaArtifactSection {
  sectionId: string;
  title: string;
  subtitle?: string;
  type: ArtifactSectionType;
  blocks: DvaArtifactBlock[];
}

// ── Artifact (Top Level) ──────────────────────────────────────

export interface DvaArtifact {
  artifactId: string;
  artifactType: ArtifactType;
  scope: ArtifactScope;
  clientId: string;
  initiativeId?: string;    // optional for enterprise-scope artifacts
  title: string;
  subtitle?: string;
  summary?: string;
  sections: DvaArtifactSection[];
  metadata: DvaArtifactMetadata;
}

// ── Typed Artifact Extensions ─────────────────────────────────

export interface ExecutiveOnePagerArtifact extends DvaArtifact {
  artifactType: "executive_one_pager";
  scope: "initiative";
}

export interface CfoRoiSummaryArtifact extends DvaArtifact {
  artifactType: "cfo_roi_summary";
  scope: "initiative";
}

export interface BoardRationaleArtifact extends DvaArtifact {
  artifactType: "board_rationale";
  scope: "initiative";
}

export interface AssumptionsAppendixArtifact extends DvaArtifact {
  artifactType: "assumptions_appendix";
  scope: "initiative";
}

export interface ImplementationPacketArtifact extends DvaArtifact {
  artifactType: "implementation_packet";
  scope: "initiative";
}

export interface StrategicExecutionPlanArtifact extends DvaArtifact {
  artifactType: "strategic_execution_plan";
  scope: "enterprise";
}

// ── Display Helpers ───────────────────────────────────────────

export const ARTIFACT_DISPLAY_LABELS: Record<ArtifactType, string> = {
  strategic_execution_plan: "Strategic Execution Plan",
  executive_one_pager:      "Executive One-Pager",
  cfo_roi_summary:          "CFO ROI Summary",
  board_rationale:          "Board Rationale",
  assumptions_appendix:     "Assumptions Appendix",
  implementation_packet:    "Implementation Packet",
};

export const ARTIFACT_STATUS_LABELS: Record<ArtifactStatus, string> = {
  draft:  "Draft",
  review: "In Review",
  final:  "Final",
};

export const ARTIFACT_STATUS_COLORS: Record<ArtifactStatus, string> = {
  draft:  "bg-amber-100 text-amber-700",
  review: "bg-blue-100 text-blue-700",
  final:  "bg-emerald-100 text-emerald-700",
};